/**
 * Moldura comum dos gráficos de série das fichas — a chuva do CEMADEN e o
 * nível da ANA se leem da mesma maneira:
 *
 * - o desenho fica PARADO; o que se move com o player é só a marca do instante
 *   do quadro, uma linha sobre o gráfico;
 * - eixo de tempo embaixo, em horário de Brasília;
 * - leitura sob o mouse (ou o toque): data, hora e valor daquela fatia. Sem
 *   mouse, a leitura é a do instante do quadro.
 *
 * Quem usa só desenha as marcas (barras, linha) dentro do `viewBox`; a
 * geometria das fatias está em `fatias.ts`.
 */
import { useState, type ReactNode } from 'react';
import { formatDayTimeBrt, formatTimeBrt } from '../lib/time';
import { centroPct, fatiaSob, marcasDoEixo } from './fatias';

/** Largura e altura do `viewBox` — o desenho estica para a largura da ficha. */
export const GRAFICO_L = 150;
export const GRAFICO_A = 34;

export interface Desenho {
  /** Largura de uma fatia no `viewBox`; o centro da fatia `i` é `(i + 0.5) * w`. */
  w: number;
  /** Fatia sob o mouse, ou `null`. */
  sob: number | null;
  /** Fatia em leitura: a do mouse ou, sem mouse, a do quadro. */
  destaque: number | null;
}

export function GraficoTempo(props: {
  /** Instante de cada fatia (epoch ms) — o que o eixo e a leitura mostram. */
  instantes: readonly number[];
  /** Fatia do quadro exibido; -1 quando o quadro está fora do período. */
  iAtual: number;
  /** Descrição para leitor de tela. */
  rotulo: string;
  /** Valor legível da fatia, ou `null` quando ela não tem medição. */
  valor: (i: number) => string | null;
  /** Resumo do período, à direita da leitura. */
  resumo: ReactNode;
  /** Marcas dentro do SVG. */
  desenho: (d: Desenho) => ReactNode;
  /** Marcas em HTML por cima do SVG, que o `preserveAspectRatio="none"` deformaria (um ponto, por exemplo). */
  sobre?: (d: Desenho) => ReactNode;
  /** Altura do desenho em px (padrão `GRAFICO_A * 1.6`). */
  altura?: number;
  /**
   * Cursor controlado de fora — o meteograma move a mesma hora em várias
   * faixas. Sem `aoSob`, cada gráfico guarda o seu, como sempre.
   */
  sob?: number | null;
  aoSob?: (i: number | null) => void;
  /** Sem o eixo de horas e a linha de leitura: quem empilha desenha uma vez só. */
  soDesenho?: boolean;
}) {
  const { instantes, iAtual, aoSob } = props;
  const n = instantes.length;
  const [sobInterno, setSobInterno] = useState<number | null>(null);
  const sob = aoSob ? (props.sob ?? null) : sobInterno;
  const setSob = aoSob ?? setSobInterno;

  const w = GRAFICO_L / Math.max(1, n);
  const temAtual = iAtual >= 0 && iAtual < n;
  const destaque = sob ?? (temAtual ? iAtual : null);
  const d: Desenho = { w, sob, destaque };
  const lido = destaque === null ? null : props.valor(destaque);
  const ler = (e: { clientX: number; currentTarget: Element }) => {
    const r = e.currentTarget.getBoundingClientRect();
    setSob(r.width > 0 ? fatiaSob((e.clientX - r.left) / r.width, n) : null);
  };

  return (
    <div>
      <div
        className="relative touch-pan-y"
        data-grafico-tempo=""
        onMouseLeave={() => setSob(null)}
        onMouseMove={ler}
        // Toque e caneta: tocar ou arrastar move o cursor, que fica onde soltou;
        // a rolagem vertical segue livre (`touch-action: pan-y`).
        onPointerDown={(e) => e.pointerType !== 'mouse' && ler(e)}
        onPointerMove={(e) => e.pointerType !== 'mouse' && ler(e)}
      >
        <svg
          viewBox={`0 0 ${GRAFICO_L} ${GRAFICO_A}`}
          width="100%"
          height={props.altura ?? GRAFICO_A * 1.6}
          preserveAspectRatio="none"
          role="img"
          aria-label={props.rotulo}
          className="block"
        >
          {props.desenho(d)}
          {/* Marca do instante do quadro: a única coisa que se move. */}
          {temAtual && (
            <line
              x1={iAtual * w + w / 2}
              y1="0"
              x2={iAtual * w + w / 2}
              y2={GRAFICO_A}
              stroke="rgba(127,227,255,0.85)"
              strokeWidth="0.8"
            />
          )}
        </svg>
        {props.sobre?.(d)}
      </div>

      {!props.soDesenho && <EixoTempo instantes={instantes} />}

      {/* Leitura: segue o mouse; sem mouse, mostra o instante do quadro. */}
      {!props.soDesenho && (
        <div className="mt-0.5 flex items-baseline justify-between gap-2 text-[10px] text-ink-400">
          <span>
            {destaque === null ? (
              'passe o mouse para ler'
            ) : (
              <>
                <span className="text-ink-200 tabular-nums">{formatDayTimeBrt(instantes[destaque]!)}</span>
                {' · '}
                {lido === null ? 'sem medição' : <span className="text-ink-100 tabular-nums">{lido}</span>}
              </>
            )}
          </span>
          <span className="text-right">{props.resumo}</span>
        </div>
      )}
    </div>
  );
}

/** Eixo de tempo em Brasília. As pontas se alinham às bordas para não vazar da ficha. */
export function EixoTempo({
  instantes,
  className = 'text-[9px]',
  perto = null,
}: {
  instantes: readonly number[];
  className?: string;
  /** Fatia do cursor: rótulos a menos de 3 fatias dela saem (a hora dele já está escrita em outro lugar). */
  perto?: number | null;
}) {
  const n = instantes.length;
  return (
    <div className={`relative mt-1 h-3 text-ink-400 ${className}`}>
      {marcasDoEixo(n)
        .filter((i) => perto === null || Math.abs(i - perto) >= 3)
        .map((i) => (
          <span
            key={i}
            className={`absolute tabular-nums ${i === 0 ? '' : i === n - 1 ? '-translate-x-full' : '-translate-x-1/2'}`}
            style={{ left: i === 0 ? 0 : i === n - 1 ? '100%' : `${centroPct(i, n)}%` }}
          >
            {formatTimeBrt(instantes[i]!)}
          </span>
        ))}
    </div>
  );
}
